import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectKanban } from "./ProjectKanban";
import { ProjectTableView } from "./ProjectTableView";

// Specific Screens
import { DesignTableView } from "../verve-design/components/DesignTableView";
import { DesignGridView } from "../verve-design/components/DesignGridView";
import DesignApprovalScreen from "../DesignApproval/screens/DesignApprovalScreen";
import FinalQuotationApprovalScreen from "../Final Quotation Approval/screens/FinalQuotationApprovalScreen";

import { projectService } from "../services/projectService";
import { showSuccessMessage, showApiError } from "@/lib/toast";

export const ProjectsTabContent = ({
  view,
  filters,
  projects,
  projectsLoading,
  user,
  teamMembers,
  salesExecs,
  projectManagers,
  
  // Modals & State Setters
  setSelectedProject,
  setIsDetailOpen,
  setIsFormOpen,
  setConfirmState,
  refetch,

  // Handlers
  toggleProjectStatus,
  handleUpdateProject,
  stages
}) => {
  const navigate = useNavigate();

  const isAll = filters.status === 'all' && user?.role !== 'designer';
  const isDesign = filters.status === 'design' || (filters.status === 'all' && user?.role === 'designer');
  const isDesignApproval = filters.status === 'design_approval';
  const isFinalQuotationApproval = filters.status === 'quotation_approval';
  
  // final_quotation, documents, project_started will fall back to All projects view (ProjectTableView)
  const isFallbackTab = !isAll && !isDesign && !isDesignApproval && !isFinalQuotationApproval;
  const showAllView = isAll || isFallbackTab;

  // Add Activity Handler (fallback)
  const handleAddActivity = (id, note, type) => {
    // This can be hooked up if needed for table view
  };

  return (
    <div className="min-h-[400px]">
      
      {/* Design Tab (Table View) */}
      {isDesign && view === "table" && (
        <div className="block">
          <DesignTableView
            designs={projects}
            onDesignClick={(design) => { navigate(`/projects/${design._id || design.id}`); }}
            onEditClick={(design) => { setSelectedProject(design); setIsFormOpen(true); }}
            updateStatus={toggleProjectStatus}
            onRequestApproval={(design) => {
              setSelectedProject(design);
              setConfirmState({
                isOpen: true,
                title: "Request Design Approval",
                message: `Are you sure you want to request design approval for ${design.customerName || design.firstName}? This will freeze the design media and submit it to the Designer and Main Designer for review.`,
                confirmText: "Submit Request",
                onConfirm: async () => {
                  try {
                    setConfirmState(prev => ({ ...prev, loading: true }));
                    const currentApproval = design.designApproval || {};
                    await projectService.update(design._id, {
                      designApproval: {
                        ...currentApproval,
                        isRequested: true,
                        designer: { status: 'pending' },
                        mainDesigner: { status: 'pending' }
                      }
                    });
                    showSuccessMessage("Design approval requested successfully");
                    refetch();
                    setConfirmState(prev => ({ ...prev, isOpen: false }));
                  } catch (e) {
                    showApiError(e);
                  } finally {
                    setConfirmState(prev => ({ ...prev, loading: false }));
                  }
                }
              });
            }}
            onToggleDisable={(design) => {
              setSelectedProject(design);
              setConfirmState({
                isOpen: true,
                title: "Delete Design",
                message: `Are you sure you want to disable this project design?`,
                onConfirm: async () => {
                  try {
                    setConfirmState(prev => ({ ...prev, loading: true }));
                    await projectService.toggleDisable(design._id, true);
                    showSuccessMessage("Design disabled successfully");
                    refetch();
                    setConfirmState(prev => ({ ...prev, isOpen: false }));
                  } catch (e) {
                    showApiError(e);
                  } finally {
                    setConfirmState(prev => ({ ...prev, loading: false }));
                  }
                }
              });
            }}
            user={user}
          />
        </div>
      )}

      {/* Design Tab (Grid View) */}
      {isDesign && view !== "table" && (
        <div className="block">
          <DesignGridView
            data={projects}
            isLoading={projectsLoading}
            onDesignClick={(design) => { navigate(`/projects/${design._id || design.id}`); }}
            onEditClick={(design) => { setSelectedProject(design); setIsFormOpen(true); }}
            onRequestApproval={(design) => {
              setSelectedProject(design);
              setConfirmState({
                isOpen: true,
                title: "Request Design Approval",
                message: `Are you sure you want to request design approval for ${design.customerName || design.firstName}? This will freeze the design media and submit it to the Designer and Main Designer for review.`,
                confirmText: "Submit Request",
                onConfirm: async () => {
                  try {
                    setConfirmState(prev => ({ ...prev, loading: true }));
                    const currentApproval = design.designApproval || {};
                    await projectService.update(design._id, {
                      designApproval: {
                        ...currentApproval,
                        isRequested: true
                      }
                    });
                    showSuccessMessage("Design approval requested successfully");
                    refetch();
                    setConfirmState(prev => ({ ...prev, isOpen: false }));
                  } catch (e) {
                    showApiError(e);
                  } finally {
                    setConfirmState(prev => ({ ...prev, loading: false }));
                  }
                }
              });
            }}
            onToggleDisable={(design) => {
              setSelectedProject(design);
              setConfirmState({
                isOpen: true,
                title: "Disable Design",
                message: `Are you sure you want to disable this design?`,
                onConfirm: async () => {
                  try {
                    setConfirmState(prev => ({ ...prev, loading: true }));
                    await projectService.toggleDisable(design._id, true);
                    showSuccessMessage("Design disabled successfully");
                    refetch();
                    setConfirmState(prev => ({ ...prev, isOpen: false }));
                  } catch (e) {
                    showApiError(e);
                  } finally {
                    setConfirmState(prev => ({ ...prev, loading: false }));
                  }
                }
              });
            }}
          />
        </div>
      )}

      {/* Design Approval Tab */}
      {isDesignApproval && view === "table" && (
        <div className="block">
          <DesignApprovalScreen 
            leads={projects} 
            isLoading={projectsLoading} 
            onRefresh={refetch} 
            onDesignClick={(design) => { navigate(`/projects/${design._id || design.id}`); }}
          />
        </div>
      )}

      {/* Final Quotation Approval Tab */}
      {isFinalQuotationApproval && view === "table" && (
        <div className="block">
          <FinalQuotationApprovalScreen />
        </div>
      )}

      {/* All Projects (Table View) */}
      {showAllView && view === "table" && (
        <div className="block">
          <ProjectTableView
            projects={projects}
            isLoading={projectsLoading}
            currentTab={filters.status}
            onProjectClick={(project) => { setSelectedProject(project); setIsDetailOpen(true); }}
            onStatusClick={(project) => { navigate(`/projects/${project._id || project.id}`); }}
            onEditClick={(project) => { setSelectedProject(project); setIsFormOpen(true); }}
            onAddActivity={handleAddActivity}
            onToggleDisable={async (project) => {
              const isEnabling = project.isDisabled;
              setConfirmState({
                isOpen: true,
                title: isEnabling ? "Enable Project" : "Disable Project",
                message: `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} this project record?`,
                variant: isEnabling ? "info" : "danger",
                confirmText: isEnabling ? "Enable Record" : "Disable Record",
                onConfirm: async () => {
                  setConfirmState(prev => ({ ...prev, loading: true }));
                  try {
                    await projectService.toggleDisable(project._id, !project.isDisabled);
                    showSuccessMessage(`Project ${isEnabling ? 'enabled' : 'disabled'} successfully`);
                    refetch();
                  } catch (err) {
                    showApiError(err);
                  } finally {
                    setConfirmState({ isOpen: false });
                  }
                }
              });
            }}
            updateStatus={toggleProjectStatus}
            onUpdateProject={handleUpdateProject}
            user={user}
            teamMembers={teamMembers}
            salesExecs={salesExecs}
            projectManagers={projectManagers}
          />
        </div>
      )}

      {/* All Projects (Kanban View) */}
      {showAllView && view !== "table" && (
        <div className="block">
          <ProjectKanban
            stages={stages}
            filtered={projects}
            isLoading={projectsLoading}
            onProjectClick={(project) => { navigate(`/projects/${project._id || project.id}`); }}
            updateStatus={toggleProjectStatus}
            onUpdateProject={handleUpdateProject}
            user={user}
            teamMembers={teamMembers}
          />
        </div>
      )}

    </div>
  );
};
